import React, { useState } from 'react';
import {
  Paper,
  Title,
  Group,
  Button,
  Table,
  ActionIcon,
  Badge,
  Text,
  TextInput,
  LoadingOverlay,
  Tooltip,
  Pagination,
  Select,
  Stack,
  Flex,
  Card,
  Menu,
  rem,
  Modal,
  Textarea,
  Timeline,
  Tabs,
  Grid,
  ThemeIcon,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconSearch,
  IconEye,
  IconCheck,
  IconX,
  IconDots,
  IconFilter,
  IconCalendar,
  IconClock,
  IconUser,
  IconCamera,
  IconMapPin,
  IconBell,
  IconShield,
  IconExclamationMark,
  IconInfoCircle,
  IconArrowUp,
  IconRefresh,
  IconPlus,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useAlerts, /* useCreateAlert, */ useUpdateAlert, useEscalateAlert } from '../../hooks/useAlerts';
// import { useStudents } from '../../hooks/useStudents';
// import { useCameras } from '../../hooks/useCameras';
import type { Alert as AlertType } from '../../types/api';


const Alerts: React.FC = () => {
  // const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState('25');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<[Date | null, Date | null]>([null, null]);

  const [selectedAlert, setSelectedAlert] = useState<AlertType | null>(null);
  const [alertModalOpened, { open: openAlertModal, close: closeAlertModal }] = useDisclosure(false);
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);
  const [escalateModalOpened, { open: openEscalateModal, close: closeEscalateModal }] = useDisclosure(false);

  const {
    data: alertsData,
    isLoading,
    error,
    refetch
  } = useAlerts({
    page,
    per_page: parseInt(perPage),
    // search: debouncedSearch,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...(severityFilter && { severity: severityFilter as 'low' | 'medium' | 'high' | 'critical' | 'info' }),
    ...(statusFilter && { status: statusFilter as 'active' | 'investigating' | 'resolved' | 'dismissed' | 'escalated' }),
    ...(dateFilter[0] && { start_date: dateFilter[0].toISOString().split('T')[0] }),
    ...(dateFilter[1] && { end_date: dateFilter[1].toISOString().split('T')[0] }),
  });

  // const { data: studentsData } = useStudents({ view: 'summary' });
  // const { data: camerasData } = useCameras({ view: 'summary' });

  // const createAlertMutation = useCreateAlert();
  const updateAlertMutation = useUpdateAlert();
  const escalateAlertMutation = useEscalateAlert();

  const alerts = alertsData?.alerts || [];
  const pagination = alertsData?.pagination;
  // const students = studentsData || [];
  // const cameras = camerasData || [];

  const handleViewAlert = (alert: AlertType) => {
    setSelectedAlert(alert);
    openAlertModal();
  };

  const handleUpdateAlertStatus = async (alert: AlertType, status: string) => {
    try {
      await updateAlertMutation.mutateAsync({
        id: alert.id,
        data: {
          status: status as any,
          resolved_at: status === 'resolved' ? new Date().toISOString() : undefined,
        } as any
      });
      notifications.show({
        title: 'Success',
        message: `Alert ${status}`,
        color: status === 'resolved' ? 'green' : 'blue',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update alert status',
        color: 'red',
      });
    }
  };

  const handleEscalateAlert = async (alert: AlertType, escalationLevel: string, notes: string) => {
    try {
      await escalateAlertMutation.mutateAsync({
        id: alert.id,
        data: {
          severity: escalationLevel as any,
          notes: notes,
        } as any
      });
      notifications.show({
        title: 'Alert Escalated',
        message: `Alert escalated to ${escalationLevel}`,
        color: 'orange',
      });
      closeEscalateModal();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to escalate alert',
        color: 'red',
      });
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      'critical': 'red',
      'high': 'orange',
      'medium': 'yellow',
      'low': 'blue',
      'info': 'gray',
    };
    return colors[severity] || 'gray';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'active': 'red',
      'investigating': 'orange',
      'resolved': 'green',
      'dismissed': 'gray',
      'escalated': 'violet',
    };
    return colors[status] || 'gray';
  };

  const getSeverityIcon = (severity: string) => {
    const icons: Record<string, React.ReactNode> = {
      'critical': <IconExclamationMark size={16} />,
      'high': <IconAlertTriangle size={16} />,
      'medium': <IconShield size={16} />,
      'low': <IconInfoCircle size={16} />,
      'info': <IconBell size={16} />,
    };
    return icons[severity] || <IconInfoCircle size={16} />;
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getResponseTime = (alert: AlertType) => {
    if (!alert.acknowledged_at) return 'Pending';
    const created = new Date(alert.created_at);
    const acknowledged = new Date(alert.acknowledged_at);
    const diffInMinutes = Math.floor((acknowledged.getTime() - created.getTime()) / (1000 * 60));
    return `${diffInMinutes}m`;
  };

  if (error) {
    notifications.show({
      title: 'Error',
      message: 'Failed to load alerts. Please try again.',
      color: 'red',
    });
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={2}>Safety Alerts</Title>
          <Text size="sm" c="dimmed">
            Monitor and manage safety incidents and system alerts
          </Text>
        </div>
        <Group>
          <Button
            variant="outline"
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
            loading={isLoading}
          >
            Refresh
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={openCreateModal}
          >
            Create Alert
          </Button>
        </Group>
      </Group>

      {/* Alert Summary Cards */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder>
            <Group>
              <ThemeIcon color="red" variant="light" size="lg">
                <IconExclamationMark size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Critical Alerts</Text>
                <Text fw={700} size="xl">
                  {alerts.filter((a: any) => a.severity === 'critical' && a.status === 'active').length}
                </Text>
              </div>
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder>
            <Group>
              <ThemeIcon color="orange" variant="light" size="lg">
                <IconAlertTriangle size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Active Alerts</Text>
                <Text fw={700} size="xl">
                  {alerts.filter((a: any) => a.status === 'active').length}
                </Text>
              </div>
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder>
            <Group>
              <ThemeIcon color="blue" variant="light" size="lg">
                <IconClock size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Avg Response Time</Text>
                <Text fw={700} size="xl">5.2m</Text>
              </div>
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder>
            <Group>
              <ThemeIcon color="green" variant="light" size="lg">
                <IconCheck size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Resolved Today</Text>
                <Text fw={700} size="xl">
                  {alerts.filter((a: any) => a.status === 'resolved' &&
                    new Date(a.resolved_at).toDateString() === new Date().toDateString()).length}
                </Text>
              </div>
            </Group>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Filters and Search */}
      <Card>
        <Stack gap="md">
          <Group>
            <TextInput
              placeholder="Search alerts..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ flexGrow: 1, maxWidth: 400 }}
            />
            <Select
              placeholder="Severity"
              leftSection={<IconAlertTriangle size={16} />}
              data={[
                { value: '', label: 'All Severities' },
                { value: 'critical', label: 'Critical' },
                { value: 'high', label: 'High' },
                { value: 'medium', label: 'Medium' },
                { value: 'low', label: 'Low' },
                { value: 'info', label: 'Info' },
              ]}
              value={severityFilter}
              onChange={(value) => setSeverityFilter(value || '')}
              style={{ minWidth: 150 }}
            />
            <Select
              placeholder="Status"
              leftSection={<IconFilter size={16} />}
              data={[
                { value: '', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'investigating', label: 'Investigating' },
                { value: 'resolved', label: 'Resolved' },
                { value: 'dismissed', label: 'Dismissed' },
                { value: 'escalated', label: 'Escalated' },
              ]}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value || '')}
              style={{ minWidth: 150 }}
            />
            <DatePickerInput
              type="range"
              placeholder="Date range"
              leftSection={<IconCalendar size={16} />}
              value={dateFilter}
              onChange={setDateFilter}
              style={{ minWidth: 200 }}
            />
          </Group>

          <Group justify="flex-end">
            <Select
              label="Per page"
              value={perPage}
              onChange={(value) => {
                setPerPage(value || '25');
                setPage(1);
              }}
              data={[
                { value: '10', label: '10' },
                { value: '25', label: '25' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
              ]}
              style={{ width: 100 }}
            />
          </Group>
        </Stack>
      </Card>

      {/* Alerts Table */}
      <Paper withBorder>
        <LoadingOverlay visible={isLoading} />

        {alerts.length === 0 && !isLoading ? (
          <Flex direction="column" align="center" justify="center" py="xl">
            <IconShield size={48} color="gray" />
            <Title order={4} c="dimmed" mt="md">
              No alerts found
            </Title>
            <Text size="sm" c="dimmed" ta="center" mt="xs">
              {search || severityFilter || statusFilter
                ? 'Try adjusting your search criteria'
                : 'All systems are running smoothly'}
            </Text>
          </Flex>
        ) : (
          <>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Alert & Severity</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Location & Source</Table.Th>
                  <Table.Th
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('created_at')}
                  >
                    Created {getSortIcon('created_at')}
                  </Table.Th>
                  <Table.Th>Response Time</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th style={{ width: 120 }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {alerts.map((alert: any) => (
                  <Table.Tr key={alert.id}>
                    <Table.Td>
                      <Group gap="sm">
                        <ThemeIcon
                          color={getSeverityColor(alert.severity)}
                          variant="light"
                          size="md"
                        >
                          {getSeverityIcon(alert.severity)}
                        </ThemeIcon>
                        <div>
                          <Text fw={500} size="sm">
                            {alert.alert_message || alert.alert_type?.replace('_', ' ') || 'Alert'}
                          </Text>
                          <Badge
                            color={getSeverityColor(alert.severity)}
                            variant="light"
                            size="xs"
                          >
                            {alert.severity}
                          </Badge>
                        </div>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={2}>
                        {alert.alert_message || alert.detection_info?.description || 'No description available'}
                      </Text>
                      {alert.alert_type && (
                        <Badge variant="outline" size="xs" mt={2}>
                          {alert.alert_type?.replace('_', ' ') || 'unknown type'}
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        {alert.location_info?.camera && (
                          <Group gap={4}>
                            <IconCamera size={12} />
                            <Text size="xs">{alert.location_info.camera}</Text>
                          </Group>
                        )}
                        {alert.location_info?.area && (
                          <Group gap={4}>
                            <IconMapPin size={12} />
                            <Text size="xs">{alert.location_info.area}</Text>
                          </Group>
                        )}
                        {alert.student && (
                          <Group gap={4}>
                            <IconUser size={12} />
                            <Text size="xs">
                              {alert.student.first_name} {alert.student.last_name}
                            </Text>
                          </Group>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">
                        {formatTimestamp(alert.created_at)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" fw={500}>
                        {getResponseTime(alert)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={getStatusColor(alert.status)}
                        variant="light"
                        size="sm"
                      >
                        {alert.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Tooltip label="View details">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => handleViewAlert(alert)}
                          >
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>

                        <Menu shadow="md" width={200}>
                          <Menu.Target>
                            <ActionIcon variant="subtle" size="sm">
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            {alert.status === 'active' && (
                              <>
                                <Menu.Item
                                  leftSection={<IconEye style={{ width: rem(14), height: rem(14) }} />}
                                  onClick={() => handleUpdateAlertStatus(alert, 'investigating')}
                                >
                                  Start Investigation
                                </Menu.Item>
                                <Menu.Item
                                  leftSection={<IconArrowUp style={{ width: rem(14), height: rem(14) }} />}
                                  color="orange"
                                  onClick={() => {
                                    setSelectedAlert(alert);
                                    openEscalateModal();
                                  }}
                                >
                                  Escalate
                                </Menu.Item>
                              </>
                            )}
                            {(alert.status === 'active' || alert.status === 'investigating') && (
                              <Menu.Item
                                leftSection={<IconCheck style={{ width: rem(14), height: rem(14) }} />}
                                color="green"
                                onClick={() => handleUpdateAlertStatus(alert, 'resolved')}
                              >
                                Mark Resolved
                              </Menu.Item>
                            )}
                            <Menu.Item
                              leftSection={<IconX style={{ width: rem(14), height: rem(14) }} />}
                              color="gray"
                              onClick={() => handleUpdateAlertStatus(alert, 'dismissed')}
                            >
                              Dismiss
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <Group justify="center" p="md">
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={pagination.total_pages}
                  size="sm"
                />
              </Group>
            )}
          </>
        )}
      </Paper>

      {/* Summary Stats */}
      {pagination && (
        <Text size="sm" c="dimmed" ta="center">
          Showing {alerts.length} of {pagination.total_count} alerts
          {(search || severityFilter || statusFilter) && ' (filtered)'}
        </Text>
      )}

      {/* Alert Detail Modal */}
      <Modal
        opened={alertModalOpened}
        onClose={closeAlertModal}
        title="Alert Details"
        size="lg"
      >
        {selectedAlert && (
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Title order={4}>{selectedAlert.title}</Title>
                <Text size="sm" c="dimmed">
                  Alert ID: {selectedAlert.id} • Created: {new Date(selectedAlert.created_at).toLocaleString()}
                </Text>
              </div>
              <Group>
                <Badge color={getSeverityColor(selectedAlert.severity)} size="lg">
                  {selectedAlert.severity}
                </Badge>
                <Badge color={getStatusColor(selectedAlert.status)} size="lg">
                  {selectedAlert.status}
                </Badge>
              </Group>
            </Group>

            <Tabs defaultValue="details">
              <Tabs.List>
                <Tabs.Tab value="details">Details</Tabs.Tab>
                <Tabs.Tab value="timeline">Timeline</Tabs.Tab>
                <Tabs.Tab value="actions">Actions</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="details" pt="md">
                <Stack gap="md">
                  <div>
                    <Text size="sm" fw={500} mb="xs">Description</Text>
                    <Text size="sm">{selectedAlert.description}</Text>
                  </div>

                  <Grid>
                    <Grid.Col span={6}>
                      {selectedAlert.camera && (
                        <div>
                          <Text size="sm" fw={500} mb="xs">Camera</Text>
                          <Text size="sm">{selectedAlert.camera.name}</Text>
                        </div>
                      )}
                    </Grid.Col>
                    <Grid.Col span={6}>
                      {selectedAlert.location_info && (
                        <div>
                          <Text size="sm" fw={500} mb="xs">Location</Text>
                          <Text size="sm">
                            {selectedAlert.location_info.area} - {selectedAlert.location_info.camera}
                          </Text>
                        </div>
                      )}
                    </Grid.Col>
                  </Grid>

                  {selectedAlert.metadata && (
                    <div>
                      <Text size="sm" fw={500} mb="xs">Additional Information</Text>
                      <pre style={{
                        fontSize: '12px',
                        background: 'var(--mantine-color-gray-0)',
                        padding: '8px',
                        borderRadius: '4px',
                        overflow: 'auto'
                      }}>
                        {JSON.stringify(selectedAlert.metadata, null, 2)}
                      </pre>
                    </div>
                  )}
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="timeline" pt="md">
                <Timeline active={1} bulletSize={24} lineWidth={2}>
                  <Timeline.Item
                    bullet={<IconAlertTriangle size={12} />}
                    title="Alert Created"
                  >
                    <Text size="xs" c="dimmed">
                      {new Date(selectedAlert.created_at).toLocaleString()}
                    </Text>
                  </Timeline.Item>

                  {selectedAlert.acknowledged_at && (
                    <Timeline.Item
                      bullet={<IconEye size={12} />}
                      title="Alert Acknowledged"
                    >
                      <Text size="xs" c="dimmed">
                        {new Date(selectedAlert.acknowledged_at).toLocaleString()}
                      </Text>
                    </Timeline.Item>
                  )}

                  {selectedAlert.resolved_at && (
                    <Timeline.Item
                      bullet={<IconCheck size={12} />}
                      title="Alert Resolved"
                    >
                      <Text size="xs" c="dimmed">
                        {new Date(selectedAlert.resolved_at).toLocaleString()}
                      </Text>
                    </Timeline.Item>
                  )}
                </Timeline>
              </Tabs.Panel>

              <Tabs.Panel value="actions" pt="md">
                <Stack gap="md">
                  <Group>
                    {selectedAlert.status === 'active' && (
                      <>
                        <Button
                          color="blue"
                          onClick={() => handleUpdateAlertStatus(selectedAlert, 'acknowledged')}
                        >
                          Acknowledge
                        </Button>
                        <Button
                          color="orange"
                          onClick={() => openEscalateModal()}
                        >
                          Escalate
                        </Button>
                      </>
                    )}
                    {(selectedAlert.status === 'active' || selectedAlert.status === 'acknowledged') && (
                      <Button
                        color="green"
                        onClick={() => handleUpdateAlertStatus(selectedAlert, 'resolved')}
                      >
                        Mark Resolved
                      </Button>
                    )}
                    <Button
                      color="gray"
                      variant="outline"
                      onClick={() => handleUpdateAlertStatus(selectedAlert, 'dismissed')}
                    >
                      Dismiss
                    </Button>
                  </Group>
                </Stack>
              </Tabs.Panel>
            </Tabs>
          </Stack>
        )}
      </Modal>

      {/* Create Alert Modal */}
      <Modal
        opened={createModalOpened}
        onClose={closeCreateModal}
        title="Create New Alert"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Alert Title"
            placeholder="Enter alert title"
            required
          />
          <Textarea
            label="Description"
            placeholder="Describe the alert"
            autosize
            minRows={3}
            required
          />
          <Select
            label="Severity"
            placeholder="Select severity"
            data={[
              { value: 'critical', label: 'Critical' },
              { value: 'high', label: 'High' },
              { value: 'medium', label: 'Medium' },
              { value: 'low', label: 'Low' },
              { value: 'info', label: 'Info' },
            ]}
            required
          />
          <Select
            label="Alert Type"
            placeholder="Select type"
            data={[
              { value: 'safety_incident', label: 'Safety Incident' },
              { value: 'system_error', label: 'System Error' },
              { value: 'camera_offline', label: 'Camera Offline' },
              { value: 'unauthorized_access', label: 'Unauthorized Access' },
              { value: 'maintenance_required', label: 'Maintenance Required' },
            ]}
            required
          />
          <Group justify="flex-end">
            <Button variant="outline" onClick={closeCreateModal}>
              Cancel
            </Button>
            <Button onClick={closeCreateModal}>
              Create Alert
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Escalate Alert Modal */}
      <Modal
        opened={escalateModalOpened}
        onClose={closeEscalateModal}
        title="Escalate Alert"
        size="md"
      >
        <Stack gap="md">
          <Select
            label="Escalation Level"
            placeholder="Select escalation level"
            data={[
              { value: 'supervisor', label: 'Supervisor' },
              { value: 'management', label: 'Management' },
              { value: 'emergency_services', label: 'Emergency Services' },
              { value: 'external_authorities', label: 'External Authorities' },
            ]}
            required
          />
          <Textarea
            label="Escalation Notes"
            placeholder="Reason for escalation and additional details"
            autosize
            minRows={3}
            required
          />
          <Group justify="flex-end">
            <Button variant="outline" onClick={closeEscalateModal}>
              Cancel
            </Button>
            <Button
              color="orange"
              onClick={() => {
                if (selectedAlert) {
                  handleEscalateAlert(selectedAlert, 'supervisor', 'Escalation notes');
                }
              }}
            >
              Escalate Alert
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default Alerts;
